export type CategorySummary = {
  articleCount: number;
  level: string;
  tags: string[];
  topicCount: number;
  learnings: { title: string; slug: string }[];
  audience: string[];
};

type CategoryOverviewCardProps = {
  summary: CategorySummary;
};

export default function CategoryOverviewCard({ summary }: CategoryOverviewCardProps) {
  return (
    <aside className="flex h-full flex-col rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex items-center justify-between gap-3">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-foreground-600">
          <span className="h-px w-5 bg-accent-500" />
          ภาพรวมหมวดนี้
        </p>
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
          <i className="ri-compass-3-line text-sm" />
        </span>
      </div>

      <div className="mt-4 flex items-end justify-between gap-3 border-b border-background-200 pb-4">
        <div className="flex items-end gap-2.5">
          <span className="font-num text-4xl font-semibold leading-none text-foreground-950">
            {summary.articleCount}
          </span>
          <span className="pb-1 text-xs text-foreground-600">บทความในหมวดนี้</span>
        </div>
        <span className="mb-1 inline-flex shrink-0 items-center gap-1.5 rounded-full bg-accent-100 px-2.5 py-1 text-xs font-semibold text-accent-900">
          <i className="ri-bar-chart-box-line text-sm" />
          {summary.level}
        </span>
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-2.5">
        <div className="min-w-0 rounded-lg border border-background-200 bg-background-100/60 px-3 py-2.5">
          <dt className="flex items-center gap-1.5 text-xs text-foreground-500">
            <i className="ri-book-read-line text-sm" />
            รูปแบบการเรียน
          </dt>
          <dd className="mt-0.5 text-xs font-semibold text-foreground-950">อ่านออนไลน์ฟรี</dd>
        </div>
        <div className="min-w-0 rounded-lg border border-background-200 bg-background-100/60 px-3 py-2.5">
          <dt className="flex items-center gap-1.5 text-xs text-foreground-500">
            <i className="ri-bookmark-3-line text-sm" />
            จำนวนหัวข้อ
          </dt>
          <dd className="mt-0.5 font-num text-sm font-semibold text-foreground-950">
            {summary.topicCount} หัวข้อ
          </dd>
        </div>
      </dl>

      <div className="mt-4 rounded-lg border border-background-200 bg-background-100/50 p-3.5">
        <p className="flex items-center gap-1.5 text-xs font-semibold text-foreground-800">
          <i className="ri-user-heart-line text-sm text-accent-600" />
          หมวดนี้เหมาะกับใคร
        </p>
        <ul className="mt-2.5 flex flex-col gap-2.5">
          {summary.audience.map((item) => (
            <li key={item} className="flex gap-2">
              <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-[10px] text-secondary-900">
                <i className="ri-check-line" />
              </span>
              <span className="min-w-0 text-xs leading-relaxed text-foreground-700">{item}</span>
            </li>
          ))}
        </ul>
      </div>

      {summary.tags.length > 0 && (
        <div className="mt-auto pt-5">
          <div className="border-t border-background-200 pt-4">
            <p className="text-xs font-semibold text-foreground-800">หัวข้อที่ครอบคลุม</p>
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {summary.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      <p className="mt-4 flex items-center gap-1.5 text-[11px] leading-relaxed text-foreground-500">
        <i className="ri-arrow-down-double-line text-sm text-secondary-600" />
        แนะนำอ่านตามลำดับด้านล่าง เพื่อให้เนื้อหาต่อเนื่องกัน
      </p>
    </aside>
  );
}